// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreview.module.css';
import type {TextualAttachmentLanguagePopoutProps} from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreviewUtils';
import {useHighlightLanguageOptions} from '@app/features/code_highlighting/utils/ArboriumHighlighting';
import {
	SearchableListPopout,
	type SearchableListPopoutItem,
	type SearchableListPopoutSection,
} from '@app/features/ui/popover/searchable_list_popout/SearchableListPopout';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';
export const TextualAttachmentLanguagePopout = observer(function TextualAttachmentLanguagePopout({
	defaultSearchQuery,
	onClose,
	onSelectLanguage,
	selectedLanguage,
}: TextualAttachmentLanguagePopoutProps) {
	const {t} = useLingui();
	const highlightLanguageOptions = useHighlightLanguageOptions();
	const handleLanguageSelect = useCallback(
		(languageCode: string) => {
			onSelectLanguage(languageCode);
			onClose();
		},
		[onClose, onSelectLanguage],
	);
	const sections = useMemo<Array<SearchableListPopoutSection>>(() => {
		const languageItems: Array<SearchableListPopoutItem> = highlightLanguageOptions.map((languageOption) => ({
			id: languageOption.code,
			ariaLabel: languageOption.code,
			searchValues: [languageOption.code, languageOption.canonicalCode],
			isSelected: languageOption.code === selectedLanguage,
			onSelect: () => {
				handleLanguageSelect(languageOption.code);
			},
			render: () => (
				<>
					<span className={styles.languageOptionCode}>{languageOption.code}</span>
					{languageOption.canonicalCode !== languageOption.code && (
						<span className={styles.languageOptionCanonical}>{languageOption.canonicalCode}</span>
					)}
				</>
			),
		}));
		return [
			{
				id: 'languages',
				items: languageItems,
			},
		];
	}, [handleLanguageSelect, highlightLanguageOptions, selectedLanguage]);
	return (
		<SearchableListPopout
			className={styles.languagePopout}
			scrollerClassName={styles.languageScroller}
			placeholder={t`Search language code...`}
			searchInputAriaLabel={t`Search language code`}
			listAriaLabel={t`Code language options`}
			defaultSearchQuery={defaultSearchQuery}
			noResultsLabel={t`No results found`}
			sections={sections}
			onRequestClose={onClose}
		/>
	);
});
